import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const createRunSchema = z.object({
  periodStart: z.string().min(1, 'Period start required'),
  periodEnd: z.string().min(1, 'Period end required'),
  notes: z.string().optional(),
})

type CreateRunFormValues = z.infer<typeof createRunSchema>

type PayrollRun = {
  id: number
  periodStart: string
  periodEnd: string
  status: string | null
  employeeCount?: number | null
  totalGross?: string | null
  totalNet?: string | null
  runAt?: Date | null
  createdAt?: Date | null
}

export const Route = createFileRoute('/hr/payroll/runs/')({
  component: PayrollRunsPage,
})

function PayrollRunsPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <PayrollRunsList />
    </ModuleGuard>
  )
}

function PayrollRunsList() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [page] = useState(1)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: runsData, isLoading } = useQuery(
    orpc.hr.listPayrollRuns.queryOptions({ input: { page, limit: 20 } }),
  )

  const runs = (runsData?.data ?? []) as unknown as PayrollRun[]

  const createMutation = useMutation(
    orpc.hr.createPayrollRun.mutationOptions({
      onSuccess: () => {
        toast.success('Payroll run created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listPayrollRuns']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const fmt = (val?: string | null) =>
    val ? Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'

  const handleSubmit = async (data: CreateRunFormValues) => {
    await createMutation.mutateAsync(data)
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payroll Runs</h1>
          <p className="text-muted-foreground">History of all payroll processing runs</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Payroll Run
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No payroll runs found. Create your first run above.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead>Gross</TableHead>
                  <TableHead>Net</TableHead>
                  <TableHead>Run Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: `/hr/payroll/runs/${run.id}` })}
                  >
                    <TableCell className="font-medium">
                      {new Date(run.periodStart).toLocaleDateString()} —{' '}
                      {new Date(run.periodEnd).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={run.status ?? 'draft'} />
                    </TableCell>
                    <TableCell>{run.employeeCount ?? '—'}</TableCell>
                    <TableCell>{fmt(run.totalGross)}</TableCell>
                    <TableCell>{fmt(run.totalNet)}</TableCell>
                    <TableCell>
                      {run.runAt
                        ? (run.runAt instanceof Date ? run.runAt : new Date(run.runAt)).toLocaleDateString()
                        : run.createdAt
                          ? (run.createdAt instanceof Date ? run.createdAt : new Date(run.createdAt)).toLocaleDateString()
                          : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateRunDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

interface CreateRunDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateRunFormValues) => Promise<void>
}

function CreateRunDialog({ open, onOpenChange, onSubmit }: CreateRunDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<CreateRunFormValues>({
    resolver: zodResolver(createRunSchema),
    defaultValues: { periodStart: '', periodEnd: '', notes: '' },
  })

  const handleSubmit = async (data: CreateRunFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>New Payroll Run</DialogTitle>
          <DialogDescription>Define the payroll period to process</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="periodStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period Start *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="periodEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period End *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional notes..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Run'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
